/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction, createSelector, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import {
  GetMyProfilePictureByIdDocument,
  GetMyProfilePictureByIdQuery,
  GetMyProfilePictureByIdQueryVariables,
  UserFragment,
} from '@school_ify/schoolify-repository/src/shared/types';
import { client } from '@school_ify/schoolify-repository/src/client';

import userAvatar from 'assets/avatar.jpg';
import { GET_FIRST_ELEMENT_IN_ARRAY, SERVICE_ENDPOINT } from 'utils/constants';

interface AuthState {
  user: UserFragment | null | undefined;
  loading: boolean;
  error: string | null;
  loadingProfilePicture: 'idle' | 'pending' | 'success' | 'failed';
  profilePicture: string;
}
const initialState: AuthState = {
  user: null,
  loading: true,
  error: null,
  loadingProfilePicture: 'idle',
  profilePicture: userAvatar,
};

// const [fetchUserProfilePicture, { data: userFiles, error }] = useGetMyProfilePictureByIdLazyQuery();
export const getProfilePicture = createAsyncThunk(
  'user/get-profile-picture-uri',
  async (userId: string) => {
    const { data: userFiles, error } = await client.query<
      GetMyProfilePictureByIdQuery,
      GetMyProfilePictureByIdQueryVariables
    >({
      query: GetMyProfilePictureByIdDocument,
      fetchPolicy: 'network-only',
      variables: {
        userId,
      },
    });

    if (error) {
      return undefined;
    }
    const bodyFormData = new FormData();
    bodyFormData.append('id', userFiles.files[GET_FIRST_ELEMENT_IN_ARRAY].id);
    const response = await axios({
      method: 'post',
      url: `${SERVICE_ENDPOINT}api/file/read`,
      data: bodyFormData,
      headers: { 'Content-Type': 'multipart/form-data', 'Access-Control-Allow-Origin': '*' },
    });

    return response.data.value;
  }
);

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAvatarUrl(state, action: PayloadAction<string>) {
      state.profilePicture = action.payload;
    },
    setUser(state, action: PayloadAction<UserFragment | null>) {
      state.error = null;
      state.user = action.payload;
      state.loading = false;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.loading = false;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(getProfilePicture.pending, state => {
      state.loadingProfilePicture = 'pending';
    });
    builder.addCase(getProfilePicture.fulfilled, (state, action) => {
      state.loadingProfilePicture = 'success';
      state.profilePicture = action.payload;
    });
    builder.addCase(getProfilePicture.rejected, (state, action) => {
      state.loadingProfilePicture = 'failed';
      state.error = action.error.message;
    });
  },
});

// Action creators are generated for each case reducer function
export const { setUser, setLoading, setError } = authSlice.actions;

const authState = (state: any) => state.auth;
export const getUser = createSelector(authState, state => state.user);
export const getLoading = createSelector(authState, state => state.loading);
export const getError = createSelector(authState, state => state.error);
export const profilePicture = createSelector(authState, state => state.profilePicture);
export const profilePictureState = createSelector(authState, state => state.loadingProfilePicture);
export const { setAvatarUrl } = authSlice.actions;

export default authSlice.reducer;
